import React, { Suspense, lazy, useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, Link  } from 'react-router-dom';
import './components/styles/App.css';

import '@fortawesome/fontawesome-free/css/all.min.css';
import OrdersList from './components/OrderDB/OrdersList';
import WOE from './components/WOE/WOE';
import Header from './components/Header';
import Login from './components/Login';
import LoginModal from './components/LoginModal';
import WorkOrderProfile from './components/WorkOrderProfile/WorkOrderProfile';
import CalendarScheduler from './components/Calendar/AppointmentCalendar';
import { isIsraelUser } from './utils/locationUtils';
import InventoryPage from './components/Inventory/InventoryPage';
import { WigTransferProvider } from './contexts/WigTransferContext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
// import RoomLaunchSidebar from './components/OrderDB/RoomLaunchSidebar';
import RoomManufacturingWithContext from './components/OrderDB/RoomManufacturingWithContext';
import { ManufacturingProvider } from './contexts/ManufacturingContext';
import { SessionManager, useActivityTracking } from './utils/sessionManager';
import CustomerSearch from './components/Customers/CustomerSearch';
import CustomerProfile from './components/Customers/CustomerProfile';
import DuplicateCustomersReport from './components/Customers/DuplicateCustomersReport';
import WigTransferModal from './components/WigTransferModal';
import AppSendText from './components/Calendar/AppSendText';
import RachelsRoom from './components/RachelsRoom/RachelsRoom';
import PastDueTasks from './components/PastDueTasks/PastDueTasks';
import SystemAdmin from './components/SystemAdmin/SystemAdmin';
// import GlobalSMSNotifications from './components/sidebars/GlobalSMSNotifications';
import SMSResponsesSidebar from './components/sidebars/SMSResponsesSidebar';
import WaitlistSidebar from './components/sidebars/Waitlist/WaitlistSidebar';
import SendOutStickyNote from './components/sidebars/SendOutStickyNote';
import CardBypassStickyNote from './components/sidebars/CardBypassStickyNote';
import TodaysAppointments from './components/Calendar/TodaysAppointments';
import RescheduledAppointments from './components/Calendar/RescheduledAppointments';
import RemindersPage from './components/Reminders/RemindersPage';
import ReminderWatcher from './components/Reminders/ReminderWatcher';
import LookoutShareWatcher from './components/PastDueTasks/LookoutShareWatcher';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import EmployeeManual from './components/EmployeeManual';
import WigRepairBalanceReport from './components/Reports/WigRepairBalanceReport';
import LeadsPage from './components/Leads/LeadsPage';
import {
  restoreAxiosAuth,
  handleLogout,
  handleSessionExpired,
  getUserData,
  setUserData,
  isSessionNearExpiry,
  getTimeUntilExpiry,
  extendSession
} from './utils/auth';

const WigPreviewPage = lazy(() => import('./components/WigPreview/WigPreviewPage'));





function App() {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
   // NEW: Session warning state
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [sessionTimeLeft, setSessionTimeLeft] = useState(0);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const pendingLoginRetry = React.useRef(null);
  const location = useLocation();

  // NEW: Activity tracking
  const { startTracking } = useActivityTracking(() => {
    handleSessionExpiredEvent(); 
  });

  // NEW: Session expiry handler
  const handleSessionExpiredEvent = () => {
    handleSessionExpired(setUser, setShowSessionWarning, () => {
      alert('Your session has expired. Please log in again.');
    });
  };

  // ✨ SIMPLIFIED: Session monitoring
  useEffect(() => {
    if (!user) return;

    const checkSession = () => {
      if (isSessionNearExpiry()) {
        const timeLeft = getTimeUntilExpiry();
        setSessionTimeLeft(Math.ceil(timeLeft / 1000 / 60)); // Convert to minutes
        setShowSessionWarning(true);
      } else {
        setShowSessionWarning(false);
      }

      // Check if session has expired
      const currentUser = getUserData();
      if (!currentUser && user) {
        handleSessionExpiredEvent();
      }
    };

    checkSession();
    const interval = setInterval(checkSession, SessionManager.SESSION_CONFIG.CHECK_INTERVAL);
    return () => clearInterval(interval);
  }, [user]);

  // NEW: Activity tracking setup
  useEffect(() => {
    if (!user) return;

    const cleanup = startTracking();
    return cleanup;
  }, [user, startTracking]);

 // ✨ SIMPLIFIED: Initial user check
  useEffect(() => {
    // Check if user is already logged in
    const loggedInUser = getUserData();
    
    if (loggedInUser) {
      setUser(loggedInUser);
      restoreAxiosAuth(loggedInUser);
    }
    
    setIsLoading(false);
  }, []);

  const handleLoginSuccess = (userData) => {
    setUser(userData);
  };
  
  const handleLogoutClick = () => {
    handleLogout(setUser, null, setShowSessionWarning);
  };

  const handleLoginRequired = (retryFn) => {
    pendingLoginRetry.current = retryFn;
    setShowLoginModal(true);
  };

  const handleModalLoginSuccess = (userData) => {
    setUser(userData);
    setShowLoginModal(false);
    if (pendingLoginRetry.current) {
      const retry = pendingLoginRetry.current;
      pendingLoginRetry.current = null;
      retry();
    }
  };

  const handleUserUpdate = (updatedUser) => {
    setUserData(updatedUser);
    setUser(updatedUser);
  };
  
  const handleExtendSession = () => {
    extendSession();
    setShowSessionWarning(false);
  };


  // Protected route component
  const ProtectedRoute = ({ children }) => {
    if (isLoading) {
      return <div>Loading...</div>;
    }
    
    if (!user) {
      return <Navigate to="/login" replace />;
    }
    
    return children;
  };
  
  if (isLoading) {
    return <div>Loading...</div>;
  }
  
  return (
    <WigTransferProvider>
    <div className="App">
      {/* Global SMS notifications for all pages (disabled for Lucie - uses sidebar instead) */}
      {/* <GlobalSMSNotifications user={user} /> */}

      {/* SMS Responses sidebar for Lucie - replaces toast notifications */}
      <SMSResponsesSidebar user={user} />

      {/* Waitlist sidebar for Lucie - manage customer waitlist */}
      <WaitlistSidebar user={user} />

      {/* Send Out sticky note for workers responsible for shipping */}
      <SendOutStickyNote user={user} />
      <CardBypassStickyNote user={user} />
      <ReminderWatcher user={user} />
      {user && <LookoutShareWatcher />}

      {user && location.pathname !== '/appointment' && <Header user={user} onLogout={handleLogoutClick} onUserUpdate={handleUserUpdate} />}

      {showSessionWarning && (
        <div className="modal" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div className="modal-content bg-white p-4 rounded shadow" style={{ maxWidth: '400px', width: '90%' }}>
            <h4 className="text-warning mb-3">
              <i className="fas fa-exclamation-triangle mr-2"></i>
              Session Expiring Soon
            </h4>
            <p>Your session will expire in <strong>{sessionTimeLeft} minute(s)</strong> due to inactivity.</p>
            <p>Would you like to extend your session?</p>
            <div className="d-flex justify-content-end gap-2">
              <button 
                className="btn btn-secondary"
                onClick={handleLogoutClick}
              >
                Logout Now
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleExtendSession}
              >
                <i className="fas fa-clock mr-1"></i>
                Extend Session
              </button>
            </div>
          </div>
        </div>
      )}
      
      {user && isIsraelUser() && (
        <div className="alert alert-info" style={{
          margin: 0,
          borderRadius: 0,
          textAlign: 'center',
          borderBottom: '1px solid #bee5eb'
        }}>
          <div className="container">
            <i className="fas fa-globe mr-2"></i>
            <strong>Israel Location Detected</strong>
            <span className="ml-2">Local processing mode.</span>
          </div>
        </div>
      )}
      
      <main>
        <Routes>
          {/* Public routes - no authentication required */}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/employee-manual" element={<EmployeeManual />} />

          <Route path="/login" element={
            user ? <Navigate to="/" replace /> : <Login onLogin={handleLoginSuccess} />
          } />
          <Route path="/" element={
            <ProtectedRoute>
              <WOE user={user} />
            </ProtectedRoute>
          } />
          <Route path="/send-text" element={
            <ProtectedRoute>
              <AppSendText />
            </ProtectedRoute>
          } />
          <Route path="/work-order-profile/:orderNo" element={
            <ProtectedRoute>
              <WorkOrderProfile currentUser={user} />
            </ProtectedRoute>
          } />
          
          <Route path="/work-order-profile" element={
            <ProtectedRoute>
              <WorkOrderProfile  currentUser={user} />
            </ProtectedRoute>
          } />
          
          {/* UPDATED: Orders page with sidebar layout */}
          <Route path="/orderdb" element={
            <ProtectedRoute>
              <OrdersList  user={user} />
            </ProtectedRoute>
          } />
          <Route path="/manufacturing/room/:roomNo" element={
              <ManufacturingProvider>
                <RoomManufacturingWithContext />
              </ManufacturingProvider>        
          } />

          <Route path="/customers" element={
            <ProtectedRoute>
              <CustomerSearch />
            </ProtectedRoute>
          } />

          <Route path="/customers/duplicates" element={
            <ProtectedRoute>
              <DuplicateCustomersReport />
            </ProtectedRoute>
          } />

          <Route path="/customers/:customerId" element={
            <ProtectedRoute>
              <CustomerProfile currentUser={user} />
            </ProtectedRoute>
          } />
          
          <Route path="/inventory" element={
            <ProtectedRoute>
              <InventoryPage user={user} />
            </ProtectedRoute>
          } />
          <Route path="/rachel-room" element={
            <ProtectedRoute>
              <RachelsRoom />
            </ProtectedRoute>
          } />
          <Route path="/past-due-tasks" element={
            <ProtectedRoute>
              <PastDueTasks />
            </ProtectedRoute>
          } />
          <Route path="/appointment" element={
            <CalendarScheduler user={user} onLoginRequired={handleLoginRequired} />
          } />
          <Route path="/today-appointments" element={
            <ProtectedRoute>
              <TodaysAppointments />
            </ProtectedRoute>
          } />
          <Route path="/rescheduled-appointments" element={
            <ProtectedRoute>
              <RescheduledAppointments />
            </ProtectedRoute>
          } />
          <Route path="/system-admin" element={
            <ProtectedRoute>
              <SystemAdmin user={user} />
            </ProtectedRoute>
          } />
          <Route path="/leads" element={
            <ProtectedRoute>
              <LeadsPage />
            </ProtectedRoute>
          } />
          <Route path="/reminders" element={
            <ProtectedRoute>
              <RemindersPage user={user} />
            </ProtectedRoute>
          } />
          <Route path="/reports/wig-repair-balances" element={
            <ProtectedRoute>
              <WigRepairBalanceReport user={user} />
            </ProtectedRoute>
          } />
          <Route path="/wig-preview" element={
            <ProtectedRoute>
              <Suspense fallback={<div>Loading...</div>}>
                <WigPreviewPage />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light" // or "dark" to match your app theme
      />
       <WigTransferModal />
      {showLoginModal && (
        <LoginModal
          onLogin={handleModalLoginSuccess}
          onClose={() => {
            setShowLoginModal(false);
            pendingLoginRetry.current = null;
          }}
        />
      )}
    </div>
    </WigTransferProvider>
  );
}

export default App;